/**
 * Licensed Materials - Property of IBM
 *
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2020, 2021
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'react',
	'prop-types',
	'@ba/ba-ui-carbon',
	'@ba/ba-ui-carbon-toolkit',
	'./LayoutPicker',
	'../../app/nls/StringResources'
], function(React, PropTypes, Carbon, Carbon_toolkit, LayoutPicker, StringResources) {
	const { Component } = React;
	const { Tabs, Tab, Button } = Carbon;
	const { FlexLayout, FlexItem } = Carbon_toolkit;


	class NavigationPickerTabs extends Component {
		constructor(props) {
			super(props);
			this.primaryButtonRef = React.createRef();
			let selectedTabIndex = this.props.navModels.findIndex(model => model.dataId === this.props.defaultNavigation);
			selectedTabIndex = selectedTabIndex < 0 ? 0 : selectedTabIndex;
			this.state = {
				navModels: this.props.navModels,
				selectedTabIndex,
				selectedLayout: null
			};
		}

		shouldComponentUpdate(nextProps, nextState) {
			const nextCustomTemplates = nextState.navModels.find(model => model.dataId === 'custom');
			const currentCustomTemplates = this.state.navModels.find(model => model.dataId === 'custom');
			if ((nextCustomTemplates && !currentCustomTemplates) || (!nextCustomTemplates && currentCustomTemplates)) {
				return true;
			}
			if (nextCustomTemplates && currentCustomTemplates && nextCustomTemplates.templates.length !== currentCustomTemplates.templates.length) {
				return true;
			}
			return false;
		}

		componentDidMount() {
			this.props.glassContext.getCoreSvc('.Events').on('deleteAction:done', this._onDeleteActionDone, this);
			if (this.primaryButtonRef.current) {
				this.primaryButtonRef.current.focus();
			}
		}

		componentWillUnmount() {
			this.props.glassContext.getCoreSvc('.Events').off('deleteAction:done', this._onDeleteActionDone, this);
		}

		render () {
			return (
				<FlexLayout className="createBoardContainer" direction='column'>
					<FlexLayout
						direction='row'
						className='createBoardTitleBar'
						justifyContent='space-between'
					>
						{this._renderTitle()}
						{this._renderButtons()}
					</FlexLayout>
					{this._renderTabSection()}
				</FlexLayout>
			);
		}

		_renderTitle() {
			const title = this.props.title || StringResources.get('createBoardTitle');
			const subTitle = this.props.subTitle || StringResources.get('createBoardSubtitle');
			return (
				<FlexLayout
					direction='column'
					className='createBoardTitle'
				>
					<FlexItem className='title-text'>{title}</FlexItem>
					<FlexItem className='subtitle-text'>{subTitle}</FlexItem>
				</FlexLayout>
			);
		}

		_renderButtons() {
			return (
				<FlexLayout
					className='createBoardBtns'
				>
					<Button
						kind='secondary'
						onClick={() => this.props.onCancel()}
					>
						{StringResources.get('cancelButton')}
					</Button>
					<Button
						kind='primary'
						onClick={() => this._onSelectTemplate()}
						ref={this.primaryButtonRef}
					>
						{StringResources.get('createButton')}
					</Button>
				</FlexLayout>
			);
		}

		_renderTabSection() {
			const renderedTabs = this.state.navModels.map(model => this._renderTab(model));
			return (
				<Tabs
					className='navigationTabs'
					selected={this.state.selectedTabIndex}
					onSelectionChange={index => this.setState({ selectedTabIndex: index })}
				>
					{renderedTabs}
				</Tabs>
			);
		}

		_renderTab(model) {
			const { dataId, label, templates } = model;
			return (
				<Tab
					id={dataId}
					label={label}
					dataId={dataId}
					renderContent={({ selected, children }) => {
						if (selected) {
							this.setState({ selectedLayout: templates[0].templateId });
							return children;
						}
						return null;
					}}
				>
					<LayoutPicker
						action={this._onSelectTemplate.bind(this)}
						showContextMenu={this.props.showContextMenu.bind(this)}
						onSelectedTemplateId={templateId => this.setState({ selectedLayout: templateId })}
						templates={templates}
						selectedNavModel={dataId}
						defaultTemplateSelected={templates[0].templateId}
					/>
				</Tab>
			);
		}

		_onSelectTemplate() {
			const selectedTab = this.state.navModels[this.state.selectedTabIndex].dataId;
			return this.props.templateLayoutGenerator.getSelectedLayoutSpec(this.state.selectedLayout)
				.then(({ layout }) => {
					this.props.action(layout, selectedTab);
				}).catch((error) => {
					this.props.displayError(error);
				});
		}

		_onDeleteActionDone (selectedContext) {
			const deletedItem = selectedContext && selectedContext.length && selectedContext[0];
			if (deletedItem && deletedItem.tags && deletedItem.tags.includes('dashboard_template')) {
				this.props.templateLayoutGenerator.customTemplateDelete(deletedItem.id);
				let navModels = this.state.navModels.map(model => ({ ...model }));
				const customNavModel = navModels.find(model => model.dataId === 'custom');
				customNavModel.templates = this.props.templateLayoutGenerator.getTemplates('custom');
				if (!customNavModel.templates.length) {
					navModels = navModels.filter(models => models.dataId !== 'custom');
					this.setState({
						navModels,
						selectedTabIndex: 0
					});
				} else {
					this.setState({ navModels });
				}
			}
		}
	}

	NavigationPickerTabs.propTypes = {
		action: PropTypes.func.isRequired,
		onCancel: PropTypes.func.isRequired,
		navModels: PropTypes.array.isRequired,
		templateLayoutGenerator: PropTypes.shape({
			initialize: PropTypes.func.isRequired,
			getTemplates: PropTypes.func.isRequired,
			customTemplateDelete: PropTypes.func.isRequired,
			getSelectedLayoutSpec: PropTypes.func.isRequired
		}),
		glassContext: PropTypes.shape({
			getCoreSvc: PropTypes.func.isRequired
		}),
		displayError: PropTypes.func,
		defaultNavigation: PropTypes.string,
		title: PropTypes.string,
		subTitle: PropTypes.string,
		showContextMenu: PropTypes.func
	};

	return NavigationPickerTabs;
});
